/**
 * ConfidencePanel.tsx - Confidence scoring display with factor breakdown
 *
 * Displays overall confidence gauge and 4-factor breakdown bars.
 * Per wobbly-nibbling-chipmunk.md Part 15.3
 */

import type { ConfidenceResult } from '@models/oracle'
import ConfidenceGauge from './ConfidenceGauge'
import './ConfidencePanel.css'

interface ConfidencePanelProps {
  confidence: ConfidenceResult
}

// Factor labels for display
const FACTOR_LABELS: Record<string, string> = {
  schema_confidence: 'Schema',
  graph_validation: 'Graph',
  chain_completeness: 'Chain',
  llm_certainty: 'LLM',
}

function ConfidencePanel({ confidence }: ConfidencePanelProps) {
  const { score, level, factors, method_modifier, adjustments, explanation } = confidence

  return (
    <div className="confidence-panel">
      <h4>Confidence</h4>

      {/* Circular gauge */}
      <ConfidenceGauge score={score} level={level} />

      {/* Factor breakdown bars */}
      <div className="confidence-factors">
        {Object.entries(factors).map(([key, value]) => (
          <div key={key} className="factor">
            <span className="factor-label">{FACTOR_LABELS[key] || key}</span>
            <div className="factor-bar">
              <div
                className="factor-fill"
                style={{ width: `${value * 100}%` }}
              />
            </div>
            <span className="factor-value">{Math.round(value * 100)}%</span>
          </div>
        ))}
      </div>

      {/* Method modifier (if not 1.0) */}
      {method_modifier !== 1.0 && (
        <div className="method-modifier">
          <span className="modifier-label">Method Modifier:</span>
          <span className="modifier-value">{method_modifier.toFixed(2)}</span>
        </div>
      )}

      {/* Adjustments (if any) */}
      {adjustments.length > 0 && (
        <div className="adjustments">
          <span className="adjustments-label">Adjustments:</span>
          <ul className="adjustments-list">
            {adjustments.map((adj, idx) => (
              <li key={idx}>{adj}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Explanation tooltip */}
      {explanation && (
        <div className="confidence-explanation">
          <p>{explanation}</p>
        </div>
      )}
    </div>
  )
}

export default ConfidencePanel
